"use client";

import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SelectCellProps {
  checked: boolean;
  onToggle: (e: React.MouseEvent) => void;
  "aria-label"?: string;
}

export function SelectCell({
  checked,
  onToggle,
  "aria-label": ariaLabel = "Select row",
}: SelectCellProps) {
  return (
    // Swallows both the checkbox click and the click base-ui replays on its
    // hidden input, so neither reaches the row's onClick.
    <div
      className="flex select-none items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        checked={checked}
        aria-label={ariaLabel}
        // Toggled from onClick so the caller keeps the MouseEvent (shift-range select).
        onClick={onToggle}
        onChange={() => undefined}
      />
    </div>
  );
}

interface SelectHeaderProps {
  checked: boolean;
  indeterminate?: boolean;
  "aria-label"?: string;
  pageCount: number;
  totalCount: number;
  /** Label of the select-everything item, e.g. "Select all candidates". */
  selectAllLabel: string;
  someSelected: boolean;
  onSelectPage: () => void;
  onSelectAll: () => void;
  onClear: () => void;
  /** Extra menu items, rendered between the select items and "Clear selection". */
  children?: ReactNode;
}

export function SelectHeader({
  checked,
  indeterminate = false,
  "aria-label": ariaLabel = "Select rows",
  pageCount,
  totalCount,
  selectAllLabel,
  someSelected,
  onSelectPage,
  onSelectAll,
  onClear,
  children,
}: SelectHeaderProps) {
  return (
    <div className="flex items-center justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="cursor-pointer" data-testid="select-header-trigger">
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              aria-label={ariaLabel}
              onChange={() => undefined}
              className="pointer-events-none"
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={6} className="min-w-[260px]">
          <DropdownMenuItem onClick={onSelectPage}>
            <span className="text-foreground">Select this page</span>
            <span className="ml-0.5 text-muted-foreground">({pageCount})</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSelectAll}>
            <span className="text-foreground">{selectAllLabel}</span>
            <span className="ml-0.5 text-muted-foreground">({totalCount})</span>
          </DropdownMenuItem>
          {children ? (
            <>
              <DropdownMenuSeparator />
              {children}
            </>
          ) : null}
          {someSelected ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onClear}>Clear selection</DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
